import { UserDetails } from "@knowt/syncing/graphql/schema";
import React from "react";
import { Flex, FlexColumn, FlexColumnAlignJustifyCenter } from "@/components/Flex";
import UserAvatar from "@/components/userCardComponents/UserAvatar";
import { themeColors } from "@/utils/themeColors";
import UserNameWithBadge from "@/components/UserNameWithBadge";
import { spacing } from "@/utils/spacing";
import { borderRadius } from "@/utils/borderRadius";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import Image from "next/image";
import { ASSETS_URL } from "@/config/deployConstants";
import Skeleton from "@mui/material/Skeleton";
import Mountable from "./styled/div/Mountable";
import { ConditionalLink } from "./ConditionalLink";

const OwnerDefaultContainer = ({ style, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <Flex
            style={{
                backgroundColor: themeColors.docsLight,
                color: themeColors.neutralBlack,
                borderRadius: borderRadius.card,
                padding: `${spacing.XL} ${spacing.MD}`,
                height: "100%",
                flex: 1,
                ...style,
            }}
            {...props}>
            {children}
        </Flex>
    );
};

const BluePill = ({ children, sx }: { children: React.ReactNode; sx?: any }) => {
    return (
        <CircularRectTextButton
            component="span"
            className="secondaryTextBold1"
            sx={{
                cursor: "inherit",
                color: themeColors.neutralWhite,
                backgroundColor: themeColors.primary4,
                padding: "1rem 2.3rem",
                ...sx,
            }}>
            {children}
        </CircularRectTextButton>
    );
};

const AnonymousOwnerCard = ({ className, style }: { className?: string; style?: React.CSSProperties }) => {
    return (
        <OwnerDefaultContainer className={className} style={{ gap: "1.6rem", alignItems: "center", ...style }}>
            <FlexColumnAlignJustifyCenter
                style={{
                    width: "fit-content",
                    backgroundColor: themeColors.neutralWhite,
                    padding: spacing.XS,
                    borderRadius: "50%",
                }}>
                <Image
                    src={`${ASSETS_URL}/images/anonymous-avatar.svg`}
                    alt="Anonymous user"
                    width={32}
                    height={32}
                    style={{ borderRadius: "50%" }}
                />
            </FlexColumnAlignJustifyCenter>
            <span
                className="bodyBold2"
                data-testid="auto-id-eaa50721-3898-4c90-a7dc-1f7b76e8c1df">Anonymous user</span>
        </OwnerDefaultContainer>
    );
};

const VerticalOwnerCard = ({
    owner,
    className,
    style,
}: {
    isSelf: boolean;
    owner: Partial<UserDetails>;
    className?: string;
    style?: React.CSSProperties;
}) => {
    if (true && owner === undefined) {
        // loading state UI
        return (
            <OwnerDefaultContainer
                className={className}
                style={{ width: "fit-content", flexDirection: "column", ...style }}>
                <FlexColumn style={{ marginBottom: spacing.XS_2 }}>
                    <Skeleton
                        animation="wave"
                        variant="circular"
                        width={"5.2rem"}
                        height={"5.2rem"}
                        style={{ flexShrink: 0, marginBottom: spacing.XS_2 }}
                    />
                    <Skeleton animation="wave" width={"60%"} height={20} />
                    <Skeleton animation="wave" width={"50%"} height={20} />
                </FlexColumn>
                <Skeleton animation="wave" height={40} width={80} />
            </OwnerDefaultContainer>
        );
    }

    return (
        <OwnerDefaultContainer
            className={className}
            style={{ width: "fit-content", flexDirection: "column", ...style }}>
            <FlexColumn style={{ marginBottom: spacing.XS_2 }}>
                <UserAvatar
                    src={owner?.pictureUrl}
                    radius={"5.2rem"}
                    alt={owner?.Name || owner?.username}
                    style={{ marginBottom: spacing.XS }}
                />
                <UserNameWithBadge
                    user={owner}
                    type="Name"
                    className="ellipsisText bodyBold2"
                    style={{ maxWidth: "20rem" }}
                />
                <span
                    className="secondaryText2"
                    data-testid="auto-id-b0645511-abcc-4863-86a7-69aba8af753f">{`@${owner?.username}`}</span>
            </FlexColumn>
            <BluePill>View profile</BluePill>
        </OwnerDefaultContainer>
    );
};

const HorizontalOwnerCard = ({
    isSelf,
    owner,
    className,
    style,
}: {
    isSelf: boolean;
    owner: Partial<UserDetails>;
    className?: string;
    style?: React.CSSProperties;
}) => {
    if (owner === undefined) {
        // loading state UI
        return (
            <OwnerDefaultContainer className={className} style={{ alignItems: "center", ...style }}>
                <Skeleton
                    animation="wave"
                    variant="circular"
                    width={"5.2rem"}
                    height={"5.2rem"}
                    style={{ flexShrink: 0, marginRight: "1rem" }}
                />
                <div
                    style={{ width: "100%" }}
                    data-testid="auto-id-e72d83ac-269c-4600-9fe0-dbd3bf5f00db">
                    <Skeleton animation="wave" width={"70%"} height={20} />
                    <Skeleton animation="wave" width={"50%"} height={20} />
                </div>
                <div
                    style={{ marginLeft: "auto" }}
                    data-testid="auto-id-af25109e-e247-487b-aa4d-ba44d2dba500">
                    <Skeleton animation="wave" height={40} width={80} />
                </div>
            </OwnerDefaultContainer>
        );
    }

    return (
        <OwnerDefaultContainer className={className} style={{ alignItems: "center", ...style }}>
            <UserAvatar
                src={owner?.pictureUrl}
                radius={"5.2rem"}
                alt={owner?.Name || owner?.username}
                style={{ marginRight: "1rem" }}
            />
            <FlexColumn style={{ marginRight: spacing.XS_2 }}>
                <UserNameWithBadge
                    user={owner}
                    type="Name"
                    className="ellipsisText bodyBold2"
                    style={{ maxWidth: "20rem" }}
                />
                <span
                    className="secondaryText2"
                    data-testid="auto-id-bbc5000f-bc82-4ef3-b5c4-f6bfb500a96e">{`@${owner?.username}`}</span>
            </FlexColumn>
            <Mountable mount={!isSelf}>
                <BluePill sx={{ marginLeft: "auto" }}>View</BluePill>
            </Mountable>
        </OwnerDefaultContainer>
    );
};

const OwnerCard = ({
    isSelf,
    owner,
    className,
    style,
    variant = "horizontal",
}: {
    isSelf: boolean;
    owner: Partial<UserDetails>;
    className?: string;
    style?: React.CSSProperties;
    variant?: "horizontal" | "vertical";
}) => {
    if (owner === null) {
        return <AnonymousOwnerCard style={{ padding: spacing.MD }} />;
    }

    if (variant === "vertical") {
        return (
            <ConditionalLink
                condition={!isSelf}
                href={`/u/${owner?.username}`}
                style={{ display: "flex", flex: style?.flex || 1 }}>
                <VerticalOwnerCard isSelf={isSelf} owner={owner} className={className} style={style} />
            </ConditionalLink>
        );
    }

    return (
        <ConditionalLink
            condition={!isSelf}
            href={`/u/${owner?.username}`}
            style={{ display: "flex", flex: style?.flex || 1 }}>
            <HorizontalOwnerCard owner={owner} isSelf={isSelf} className={className} style={style} />
        </ConditionalLink>
    );
};

export default OwnerCard;
